import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { BaseLabel, CustomSingleSelect } from '@mgm/common/components/base';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getDorLocations, getIsOpenDorDialog } from '../../../store/settings/selectors';
import { settingsActions } from '../../../store/settings/slice';
import { useGetDorLocationsAPI, useSetLocationMappingAPI } from '../../../hooks/api/useIntegrations';
import { getPlatformData } from '../../../store/common/selectors';
import { TLocation } from '../../../types';

const DorLocationsDialog = () => {
  const dispatch = useAppDispatch();
  const { locations: shopifyLocations } = useAppSelector(getPlatformData);
  const dorLocations = useAppSelector(getDorLocations);
  const isOpenDorDialog = useAppSelector(getIsOpenDorDialog);
  const { t } = useTranslation(['settings', 'common']);
  const { mutateAsync: setLocations, isLoading } = useSetLocationMappingAPI();

  useGetDorLocationsAPI();

  const methods = useForm({
    mode: 'all',
  });

  const [shopifyLocationMap, setShopifyLocationMap] = useState<{ [key: string]: TLocation }>({});

  useEffect(() => {
    const initialMap: { [key: string]: TLocation } = {};
    dorLocations.forEach((dorLocation) => {
      initialMap[dorLocation.id] = {} as TLocation;
    });
    setShopifyLocationMap(initialMap);
  }, [dorLocations]);

  const handleShopifyChange = (dorLocationId: string, selectedShopifyLocationId: TLocation | null) => {
    setShopifyLocationMap((prev: any) => ({
      ...prev,
      [dorLocationId]: selectedShopifyLocationId,
    }));
  };

  const onClose = () => {
    dispatch(settingsActions.setIsOpenDorDialog(false));
  };

  const onSave = async () => {
    const mappedLocations = dorLocations.map((dorLocation) => {
      const selectedLocation = shopifyLocationMap[dorLocation.id];

      const shopifyLocation = shopifyLocations.find((location) => String(location.id) === String(selectedLocation?.id));

      return {
        shopifyLocationId: String(shopifyLocation?.id) || '',
        shopifyLocationName: shopifyLocation?.name || '',
        dorLocationId: dorLocation.id,
        dorLocationName: dorLocation.name,
      };
    });

    const data = {
      locationsMapping: mappedLocations,
    };

    await setLocations(data);
  };

  return (
    <Dialog open={isOpenDorDialog} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('assignLocations')}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <Box pb={2}>
            <BaseLabel letterSpacing="-0.5px">Dor Locations</BaseLabel>
          </Box>
          {dorLocations.map((dorLocation) => (
            <Box
              key={dorLocation.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box flex="1" pr={2}>
                <Typography variant="body1" fontWeight="bold">
                  {dorLocation.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {dorLocation.address}
                </Typography>
              </Box>

              <Box flex="1">
                <CustomSingleSelect
                  value={shopifyLocationMap[dorLocation.id] as unknown as TLocation}
                  onChange={(value) => handleShopifyChange(dorLocation.id, value)}
                  options={shopifyLocations}
                  name={`shopifyLocation-${dorLocation.id}`}
                  isRequired
                />
              </Box>
            </Box>
          ))}
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <LoadingButton disabled={isLoading} onClick={onClose} variant="outlined" color="primary">
          {t('cancel', { ns: 'common' })}
        </LoadingButton>
        <LoadingButton loading={isLoading} disabled={isLoading} onClick={onSave} variant="contained" color="primary">
          {t('save', { ns: 'common' })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export { DorLocationsDialog };
